import { AppIcon } from '@/components/ui/app-icon';
import {
  useQuestionCardInput,
  type RequirementReviewInput,
} from './question-card-input-context';
import {
  AGENT_CARD_BUTTON_DARK,
  AGENT_CARD_BUTTON_OUTLINE,
  AgentCard,
  CARD_LINK,
} from '@/components/agent-chat/agent-card.component';
import {
  CardMeta,
  CardRow,
  CardRows,
  CardStatus,
  CardText,
} from '@/components/agent-chat/card-parts.component';
import {
  composeAnswersMessage,
  emptyAnswer,
  followingUserText,
  followingUserInput,
  isLastQuestionCardOf,
  isQuestionAnswered,
  optionLetter,
  parseQuestionCard,
  questionCardState,
  SKIPPED_QUESTIONS_MESSAGE,
  slotWords,
  WORKSTREAM_REJECTED_MESSAGE,
  type CardQuestion,
  type QuestionAnswer,
  type QuestionCardData,
} from '@/components/agent-chat/question-card.util';
import { StepRow } from '@/components/agent-chat/step-row.component';
import { questionReviewSummary } from '@/containers/agents/review-summary.util';
import { iconCheck, iconCircleQuestion } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { useAppDispatch } from '@/store';
import { sendAiChatMessage } from '@/store/features/ui.feature';
import { useAuiState } from '@assistant-ui/react';
import {
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

export const QUESTION_CARD_TITLE = 'Question';
/** The review-only card's title. */
const REVIEW_PLAN_TITLE = 'Review plan';
/** The confirm button of a requirement review. */
const BUILD_IT_LABEL = 'Build it';
/** The folded row's label: the same words as the ask's step. */
const ANSWERED_QUESTION_LABEL = 'Asking for your input';

/** One row per option, and Other last when the question allows it. */
interface OptionRow {
  key: string;
  label: string;
  letter: string;
  isOther: boolean;
}

function rowsOf(question: CardQuestion): OptionRow[] {
  const rows: OptionRow[] = question.options.map((option, index) => ({
    key: option.key,
    label: option.label,
    letter: optionLetter(index),
    isOther: false,
  }));
  if (question.other) {
    rows.push({
      key: '__other__',
      label: 'Other',
      letter: optionLetter(rows.length),
      isOther: true,
    });
  }
  return rows;
}

function isTextField(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLInputElement &&
      target.type !== 'radio' &&
      target.type !== 'checkbox')
  );
}

/**
 * CardFooter's layout (a link or Back at the left, the buttons at the right)
 * with the card's own buttons. CardFooter's main button locks the card after one
 * click and runs its action a tick later; Next only moves a step, and a
 * review whose send fails is handed back enabled by its part, so these
 * buttons keep their plain clicks.
 */
function QuestionFooter({
  start,
  children,
}: {
  start?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className='flex flex-wrap items-center gap-[8px]'>
      {start ? (
        <div className='flex min-w-0 items-center gap-[12px]'>{start}</div>
      ) : null}
      <div className='ml-auto flex items-center gap-[8px]'>{children}</div>
    </div>
  );
}

/** Shows or hides a review's evidence, which stays closed until asked. */
function DetailsLink({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type='button'
      aria-expanded={open}
      onClick={onToggle}
      className={CARD_LINK}
    >
      {open ? 'Hide details' : 'Details'}
    </button>
  );
}

/**
 * The structured question card, one question at a time: the header with the
 * progress, the question with a red star and its one line detail, lettered
 * option rows (a radio each, a checkbox when several may be picked, Other
 * turning into a text field), Back, then Skip and Next, which reads Submit
 * on the last question. A requirement review shows only its questions, then
 * moves to the Review plan step with its rows and evidence, where Build it
 * sends the answers.
 *
 * Keys while the card has focus: the option letters pick, Enter is Next,
 * Cmd+Enter submits (or moves to Review plan), Cmd+Shift+Backspace skips.
 */
type QuestionCardProps = {
  card: QuestionCardData;
  onSubmit: (message: string, input?: RequirementReviewInput) => void;
  disabled?: boolean;
};

function reviewInput(
  card: QuestionCardData,
  answers: QuestionAnswer[],
  decision: 'confirmed' | 'rejected'
): RequirementReviewInput {
  return {
    type: 'requirement_review',
    card_id: card.cardId,
    revision: card.requirementReview!.revision,
    decision,
    answers:
      decision === 'rejected'
        ? []
        : card.questions.map((q, i) => ({
            question_id: q.id,
            selected_keys: answers[i]?.picked ?? [],
            ...(answers[i]?.other !== null && answers[i]?.other !== undefined
              ? { other: answers[i].other!.trim() }
              : {}),
          })),
  };
}

function requirementSourceWords(source: string | undefined) {
  if (!source) return undefined;
  if (source === 'user') return 'User request';
  if (source === 'clu') return 'Clu restatement';
  return source;
}

/**
 * One line of a review's evidence in a row's frame. Unlike CardRow it wraps:
 * a requirement the user confirms is read in full, never cut.
 */
function EvidenceRow({
  label,
  text,
  note,
}: {
  label?: string;
  text: string;
  note?: string;
}) {
  return (
    <div className='flex w-full min-w-0 flex-col gap-[4px] rounded-[8px] border border-border bg-background px-[10px] py-[6px] text-[12px] leading-[16px]'>
      <p className='flex min-w-0 flex-wrap gap-x-[8px] text-foreground'>
        {label ? <span className='text-muted-foreground'>{label}</span> : null}
        <span className='min-w-0 font-medium break-words whitespace-pre-wrap'>
          {text}
        </span>
      </p>
      {note ? <p className='text-muted-foreground'>{note}</p> : null}
    </div>
  );
}

/**
 * A review's few label and value rows, and its evidence below them while
 * Details is open: the earlier requests the build is held to, each
 * requirement, each resolved choice in words, and the source request.
 */
function RequirementSummary({
  card,
  open,
}: {
  card: QuestionCardData;
  open: boolean;
}) {
  const review = card.requirementReview;
  if (!review) return null;
  const rows = questionReviewSummary(
    review.requirements.length,
    review.resolvedSlots.length
  );
  return (
    <div className='flex flex-col gap-[8px]'>
      <CardRows label='Workstream summary'>
        {rows.map((row) => (
          <CardRow key={row.label} label={row.label} end={row.state} />
        ))}
      </CardRows>
      {open ? (
        <CardRows label='Requirement evidence'>
          {review.carried.map((line, index) => (
            <EvidenceRow
              key={`carried-${index}`}
              text={line}
              note='Earlier request'
            />
          ))}
          {review.requirements.map((item) => (
            <EvidenceRow
              key={item.id}
              text={item.text}
              note={requirementSourceWords(item.source)}
            />
          ))}
          {review.resolvedSlots.map((slot) => {
            const words = slotWords(slot);
            return (
              <EvidenceRow
                key={slot.key}
                label={slot.label}
                text={words.value}
                note={words.source}
              />
            );
          })}
          {review.sourceText ? (
            <EvidenceRow text={review.sourceText} note='Source request' />
          ) : null}
        </CardRows>
      ) : null}
    </div>
  );
}

export function QuestionCard(props: QuestionCardProps) {
  if (props.card.questions.length)
    return <QuestionCardWithQuestions {...props} />;
  if (!props.card.requirementReview) return null;
  return <ReviewPlanCard {...props} />;
}

/**
 * A requirement review with nothing left to ask: the rows, Reject and Build
 * it. After a review's questions it carries their answers and Back.
 */
function ReviewPlanCard({
  card,
  onSubmit,
  disabled,
  answers = [],
  onBack,
}: QuestionCardProps & {
  answers?: QuestionAnswer[];
  onBack?: () => void;
}) {
  const [showDetails, setShowDetails] = useState(false);
  const submit = () => {
    if (!disabled)
      onSubmit(
        composeAnswersMessage(card, answers),
        reviewInput(card, answers, 'confirmed')
      );
  };
  const reject = () => {
    if (!disabled)
      onSubmit(WORKSTREAM_REJECTED_MESSAGE, reviewInput(card, [], 'rejected'));
  };
  return (
    <AgentCard
      role='group'
      aria-label={REVIEW_PLAN_TITLE}
      icon={iconCircleQuestion}
      title={REVIEW_PLAN_TITLE}
      data-question-card
      tabIndex={-1}
      // Reached from the questions, it takes their focus so the keys keep
      // working.
      autoFocus={!!onBack}
      className='outline-hidden'
      onKeyDown={(event) => {
        if (!(event.metaKey || event.ctrlKey)) return;
        if (event.key === 'Enter') {
          event.preventDefault();
          submit();
        }
        if (event.shiftKey && event.key === 'Backspace') {
          event.preventDefault();
          reject();
        }
      }}
    >
      <RequirementSummary card={card} open={showDetails} />
      <QuestionFooter
        start={
          <>
            {onBack ? (
              <button
                type='button'
                onClick={onBack}
                disabled={disabled}
                className={AGENT_CARD_BUTTON_OUTLINE}
              >
                Back
              </button>
            ) : null}
            <DetailsLink
              open={showDetails}
              onToggle={() => setShowDetails((open) => !open)}
            />
          </>
        }
      >
        <button
          type='button'
          disabled={disabled}
          className={AGENT_CARD_BUTTON_OUTLINE}
          onClick={reject}
        >
          Reject
        </button>
        <button
          type='button'
          disabled={disabled}
          className={AGENT_CARD_BUTTON_DARK}
          onClick={submit}
        >
          {BUILD_IT_LABEL}
        </button>
      </QuestionFooter>
    </AgentCard>
  );
}

function QuestionCardWithQuestions({
  card,
  onSubmit,
  disabled = false,
}: QuestionCardProps) {
  const baseId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(0);
  // A requirement review ends on the Review plan step, where it is built.
  const [reviewing, setReviewing] = useState(false);
  const [answers, setAnswers] = useState<QuestionAnswer[]>(() =>
    card.questions.map(() => emptyAnswer())
  );

  const total = card.questions.length;
  const question = card.questions[Math.min(step, total - 1)];
  const answer = answers[step] ?? emptyAnswer();
  const rows = useMemo(() => rowsOf(question), [question]);
  const isLast = step >= total - 1;
  const answered = isQuestionAnswered(answer);
  const allAnswered = card.questions.every((_, index) =>
    isQuestionAnswered(answers[index])
  );

  const setAnswer = useCallback(
    (next: QuestionAnswer) => {
      setAnswers((current) =>
        current.map((item, index) => (index === step ? next : item))
      );
    },
    [step]
  );

  const pick = useCallback(
    (row: OptionRow) => {
      if (disabled) return;
      if (question.multiple) {
        if (row.isOther) {
          setAnswer({
            picked: answer.picked,
            other: answer.other === null ? '' : null,
          });
          return;
        }
        const has = answer.picked.includes(row.key);
        setAnswer({
          picked: has
            ? answer.picked.filter((key) => key !== row.key)
            : [...answer.picked, row.key],
          other: answer.other,
        });
        return;
      }
      if (row.isOther) {
        setAnswer({ picked: [], other: answer.other ?? '' });
        return;
      }
      setAnswer({ picked: [row.key], other: null });
    },
    [answer, disabled, question.multiple, setAnswer]
  );

  const keepFocus = useCallback(() => {
    // After moving between questions, focus stays in the card so the keys
    // keep working.
    requestAnimationFrame(() => rootRef.current?.focus());
  }, []);

  const goNext = useCallback(() => {
    if (disabled || isLast || !answered) return;
    setStep((current) => Math.min(current + 1, total - 1));
    keepFocus();
  }, [answered, disabled, isLast, keepFocus, total]);

  const goBack = useCallback(() => {
    if (disabled || step === 0) return;
    setStep((current) => Math.max(current - 1, 0));
    keepFocus();
  }, [disabled, keepFocus, step]);

  const submit = useCallback(() => {
    if (disabled || !allAnswered) return;
    if (card.requirementReview) setReviewing(true);
    else onSubmit(composeAnswersMessage(card, answers));
  }, [allAnswered, answers, card, disabled, onSubmit]);

  const reject = useCallback(() => {
    if (disabled) return;
    if (card.requirementReview)
      onSubmit(WORKSTREAM_REJECTED_MESSAGE, reviewInput(card, [], 'rejected'));
    else onSubmit(SKIPPED_QUESTIONS_MESSAGE);
  }, [card, disabled, onSubmit]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const mod = event.metaKey || event.ctrlKey;
    if (mod && event.shiftKey && event.key === 'Backspace') {
      event.preventDefault();
      reject();
      return;
    }
    if (event.key === 'Enter') {
      if (mod) {
        event.preventDefault();
        submit();
        return;
      }
      // A focused button (Back included) answers Enter itself.
      if (event.target instanceof HTMLButtonElement) return;
      event.preventDefault();
      if (!isLast) goNext();
      return;
    }
    if (mod || event.altKey || isTextField(event.target)) return;
    if (event.key.length !== 1) return;
    const row = rows.find(
      (item) => item.letter.toLowerCase() === event.key.toLowerCase()
    );
    if (row) {
      event.preventDefault();
      pick(row);
    }
  };

  const questionId = `${baseId}-question`;

  if (reviewing)
    return (
      <ReviewPlanCard
        card={card}
        onSubmit={onSubmit}
        disabled={disabled}
        answers={answers}
        onBack={() => {
          setReviewing(false);
          keepFocus();
        }}
      />
    );

  return (
    <AgentCard
      ref={rootRef}
      role='group'
      aria-label={QUESTION_CARD_TITLE}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      data-question-card
      className='outline-hidden'
      icon={iconCircleQuestion}
      title={QUESTION_CARD_TITLE}
      trailing={
        <CardStatus>
          {step + 1} of {total}
        </CardStatus>
      }
    >
      <div className='flex flex-col gap-[8px]'>
        <div className='flex flex-col gap-[4px]'>
          <div id={questionId}>
            <CardText lines={0}>
              {question.question}
              <span className='text-destructive'> *</span>
            </CardText>
          </div>
          {question.detail ? <CardMeta>{question.detail}</CardMeta> : null}
        </div>
        <div
          role={question.multiple ? 'group' : 'radiogroup'}
          aria-labelledby={questionId}
          className='flex flex-col gap-[4px]'
        >
          {rows.map((row) => {
            const checked = row.isOther
              ? answer.other !== null
              : answer.picked.includes(row.key);
            return (
              <OptionRowView
                key={`${question.id}-${row.key}`}
                name={`${baseId}-${question.id}`}
                row={row}
                multiple={question.multiple}
                checked={checked}
                disabled={disabled}
                otherText={row.isOther ? answer.other : null}
                onPick={() => pick(row)}
                onOtherChange={(text) =>
                  setAnswer({ picked: answer.picked, other: text })
                }
              />
            );
          })}
        </div>
      </div>

      <QuestionFooter
        start={
          step > 0 ? (
            <button
              type='button'
              onClick={goBack}
              disabled={disabled}
              className={AGENT_CARD_BUTTON_OUTLINE}
            >
              Back
            </button>
          ) : null
        }
      >
        <button
          type='button'
          onClick={reject}
          disabled={disabled}
          className={AGENT_CARD_BUTTON_OUTLINE}
        >
          {/* On a plan review this rejects the whole workstream, so say so. */}
          {card.requirementReview ? 'Reject' : 'Skip'}
        </button>
        {isLast ? (
          <button
            type='button'
            onClick={submit}
            disabled={disabled || !allAnswered}
            className={AGENT_CARD_BUTTON_DARK}
          >
            {card.requirementReview ? 'Next' : 'Submit'}
          </button>
        ) : (
          <button
            type='button'
            onClick={goNext}
            disabled={disabled || !answered}
            className={AGENT_CARD_BUTTON_DARK}
          >
            Next
          </button>
        )}
      </QuestionFooter>
    </AgentCard>
  );
}

function OptionRowView({
  name,
  row,
  multiple,
  checked,
  disabled,
  otherText,
  onPick,
  onOtherChange,
}: {
  name: string;
  row: OptionRow;
  multiple: boolean;
  checked: boolean;
  disabled: boolean;
  otherText: string | null;
  onPick: () => void;
  onOtherChange: (text: string) => void;
}) {
  const writing = row.isOther && checked;
  // CardRow's frame and selected look, with the letter key at the left.
  return (
    <label
      data-checked={checked ? 'true' : undefined}
      className={cn(
        'flex min-h-[30px] w-full min-w-0 cursor-pointer items-center gap-[8px] rounded-[8px] border py-[4px] pr-[10px] pl-[4px] text-left text-[12px] leading-[16px] text-foreground transition-colors',
        checked
          ? 'border-foreground/60 bg-muted/60'
          : 'border-border bg-background',
        !checked && !disabled && 'hover:bg-muted/60',
        disabled && 'cursor-default opacity-50'
      )}
    >
      <input
        type={multiple ? 'checkbox' : 'radio'}
        name={name}
        className='sr-only'
        checked={checked}
        disabled={disabled}
        onChange={onPick}
        aria-label={row.label}
      />
      <span
        aria-hidden='true'
        className={cn(
          'grid size-[20px] shrink-0 place-items-center rounded-[6px] border bg-background text-center font-mono text-[12px] leading-[16px] uppercase tabular-nums transition-colors',
          checked
            ? 'border-foreground/60 text-foreground'
            : 'border-border text-muted-foreground'
        )}
      >
        {row.letter}
      </span>
      {writing ? (
        <input
          type='text'
          autoFocus
          value={otherText ?? ''}
          onChange={(event) => onOtherChange(event.target.value)}
          disabled={disabled}
          placeholder='Other'
          aria-label='Your answer'
          className='min-w-0 flex-1 bg-transparent text-[12px] leading-[16px] font-medium text-foreground outline-hidden placeholder:text-muted-foreground/70'
        />
      ) : (
        <span className='min-w-0 flex-1 font-medium break-words'>
          {row.label}
        </span>
      )}
      <span
        aria-hidden='true'
        className={cn(
          'ml-auto grid size-[16px] shrink-0 place-items-center border',
          multiple ? 'rounded-[4px]' : 'rounded-full',
          checked ? 'border-foreground/60' : 'border-border',
          multiple && checked && 'bg-foreground text-background'
        )}
      >
        {checked ? (
          multiple ? (
            <AppIcon icon={iconCheck} style={{ fontSize: 9 }} />
          ) : (
            <span className='size-[8px] rounded-full bg-foreground' />
          )
        ) : null}
      </span>
    </label>
  );
}

/** The card once answered, skipped or passed over: one step row (12/16). */
export function AnsweredQuestionCard({ lines }: { lines: string[] }) {
  return (
    <StepRow
      icon={iconCircleQuestion}
      label={ANSWERED_QUESTION_LABEL}
      details={lines}
    />
  );
}

/**
 * The `question_card` display in a message: Clu's lead-in line above it,
 * then the card while it waits for the user, or one step row once the next
 * user message answers or passes it. Only the last card of a message with
 * no user message after it is live.
 * Submitting sends the answers into the current conversation through
 * `sendAiChatMessage`.
 */
export function QuestionCardPart({
  toolCallId,
  display,
  result,
}: {
  toolCallId: string;
  display: unknown;
  result: unknown;
}) {
  const dispatch = useAppDispatch();
  const inputContext = useQuestionCardInput();
  const card = useMemo(
    () => parseQuestionCard(display, result),
    [display, result]
  );
  const followingText = useAuiState((state) =>
    followingUserText(state.thread.messages, state.message.id)
  );
  const userInput = useAuiState((state) =>
    followingUserInput(state.thread.messages, state.message.id)
  );
  const isLastCard = useAuiState((state) =>
    isLastQuestionCardOf(state.message, toolCallId)
  );
  const sentRef = useRef(false);
  const [sent, setSent] = useState(false);

  const onSubmit = useCallback(
    async (message: string, input?: RequirementReviewInput) => {
      if (sentRef.current) return;
      if (
        card?.requirementReview &&
        (!input ||
          !inputContext ||
          inputContext.isStreaming ||
          inputContext.isLoadingConversation ||
          inputContext.conversationId !== card.requirementReview.conversationId)
      )
        return;
      sentRef.current = true;
      setSent(true);
      if (input && card?.requirementReview && inputContext) {
        try {
          const result = await inputContext.send(
            message,
            input,
            card.requirementReview.conversationId
          );
          if (result && !result.ok && !result.aborted) {
            sentRef.current = false;
            setSent(false);
          }
        } catch {
          sentRef.current = false;
          setSent(false);
        }
      } else dispatch(sendAiChatMessage(message));
    },
    [card, dispatch, inputContext]
  );

  if (!card) return null;
  const state = questionCardState(card, followingText, isLastCard, userInput);
  return (
    <div className='flex flex-col gap-[8px]'>
      {card.leadIn ? <QuestionCardLeadIn text={card.leadIn} /> : null}
      {state.kind !== 'open' ? (
        <AnsweredQuestionCard lines={state.lines} />
      ) : (
        <QuestionCard
          card={card}
          onSubmit={onSubmit}
          disabled={
            sent ||
            (!!card.requirementReview &&
              (inputContext?.isLoadingConversation ||
                inputContext?.isStreaming ||
                inputContext?.conversationId !==
                  card.requirementReview.conversationId))
          }
        />
      )}
    </div>
  );
}

/** The line Clu wrote to go with the card, above it like its answer text. */
function QuestionCardLeadIn({ text }: { text: string }) {
  return (
    <p
      className='text-[14px] leading-[22.75px] break-words text-foreground'
      data-question-lead-in
    >
      {text}
    </p>
  );
}
